import React, { useState, useEffect, useMemo } from 'react';
import './style.scss';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ProgressMessage from 'components/Utils/ProgressMessage';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import PerformanceUtils from 'services/PerformanceUtils';
import { DefaultSwitch } from 'services/UIUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import MenuItem from '@material-ui/core/MenuItem';

// Ops-only plan configuration. Booking access is gated by the delivery-option
// "subscribers only" flag and per-concern fees — this screen only controls how
// plans are presented (visibility, card bullets, badge) and the minimum
// commitment that drives the contract-signing featureset.
const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public (all patients)' },
  { value: 'internal', label: 'Internal (staff/test accounts only)' },
  { value: 'hidden', label: 'Hidden' },
];

const PaymentPlanSetup = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    setLoading(true);
    dispatch(api.Payments.getAllPaymentPlans()).then((result) => {
      setLoading(false);
      if (result) setPlans(result);
    });
  }, [dispatch]);

  function onChange(planId, field, value) {
    let modified = PerformanceUtils.clone(plans).map((plan) => {
      if (plan.id === planId) {
        plan[field] = value;
      }
      return plan;
    });
    setPlans(modified);
  }

  async function savePaymentPlans() {
    let clinicPaymentPlans = PerformanceUtils.clone(plans);
    setSaving(true);

    clinicPaymentPlans = clinicPaymentPlans.map((plan) => ({
      id: plan.id,
      visibility: plan.visibility,
      highlight: plan.highlight,
      minimum_months: plan.minimum_months,
      features: (plan.features || []).filter((feature) => !!feature.trim()),
    }));

    const result = await dispatch(api.Payments.editPaymentPlans({ clinicPaymentPlans }), {
      title: 'Payment Plans',
      successText: 'Payment plans saved!',
    });
    setSaving(false);
    if (result) setPlans(result);
  }

  const archived = useMemo(() => {
    return plans.filter((plan) => !plan.payment_plan.active);
  }, [plans]);

  const active = useMemo(() => {
    return plans.filter((plan) => !!plan.payment_plan.active);
  }, [plans]);

  return (
    <div className="payment-plan-setup full-width">
      <ProgressMessage show={loading && plans.length === 0} />
      {active.length > 0 && <span className="group-title">Active</span>}
      {active.map((plan) => (
        <Plan key={plan.id} plan={plan} onChange={onChange} />
      ))}
      {archived.length > 0 && <span className="group-title">Archived</span>}
      {archived.map((plan) => (
        <Plan key={plan.id} plan={plan} onChange={onChange} />
      ))}
      <div className="row save-row">
        <DefaultButton name="Save" className="blue" onClick={savePaymentPlans} loading={saving} />
      </div>
    </div>
  );
};

const Plan = ({ plan, onChange }) => {
  const isMonthly = plan.payment_plan.interval === 'month';
  const features = plan.features || [];

  return (
    <div className="plan">
      <span className="plan-name">
        {plan.payment_plan.name} - {plan.payment_plan.interval || 'pay-per-use'}
      </span>
      <div className="row permissions">
        <DefaultSelectField
          name={plan.id + '-visibility'}
          label="Visibility"
          value={plan.visibility || 'hidden'}
          onChange={(event) => onChange(plan.id, 'visibility', event.target.value)}
          style={{ width: '250px' }}
          inputElements={VISIBILITY_OPTIONS.map((option) => (
            <MenuItem value={option.value} key={plan.id + '-visibility-' + option.value}>
              {option.label}
            </MenuItem>
          ))}
        />
        <DefaultSwitch
          name={plan.id + '-highlight'}
          label="Highlight (most popular)"
          checked={!!plan.highlight}
          onChange={(event, checked) => onChange(plan.id, 'highlight', checked)}
        />
        {isMonthly && (
          <div className="minimum-months-field">
            <DefaultTextField
              label="Minimum commitment (months)"
              name={plan.id + '-minimum-months'}
              type="number"
              value={plan.minimum_months ?? ''}
              onChange={(event) =>
                onChange(
                  plan.id,
                  'minimum_months',
                  event.target.value === '' ? undefined : parseInt(event.target.value),
                )
              }
              style={{ width: '220px' }}
            />
            <span className="field-hint">
              Applies to monthly subscriptions. Leave empty to disable the commitment period and
              contract signing for this plan.
            </span>
          </div>
        )}
      </div>
      <div className="features-editor">
        <span className="features-title">Plan card bullets</span>
        {features.map((feature, index) => (
          <div className="row feature-row" key={plan.id + '-feature-' + index}>
            <DefaultTextField
              name={plan.id + '-feature-' + index}
              value={feature}
              onChange={(event) => {
                const updated = [...features];
                updated[index] = event.target.value;
                onChange(plan.id, 'features', updated);
              }}
              style={{ width: '400px' }}
            />
            <DefaultButton
              name="Remove"
              className="red"
              onClick={() =>
                onChange(
                  plan.id,
                  'features',
                  features.filter((_, i) => i !== index),
                )
              }
            />
          </div>
        ))}
        <DefaultButton
          name="Add Bullet"
          disabled={features.length >= 10}
          onClick={() => onChange(plan.id, 'features', [...features, ''])}
        />
      </div>
    </div>
  );
};

export default React.memo(PaymentPlanSetup);
